import React from 'react'
import { Box, Container, Grid } from '@mui/material'

import type { HolidayNavTab, HolidayPackage } from '@/src/types/holidays'
import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import HolidaysFooter from '@/src/components/desktop/homepageFooter'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { HOLIDAYS_MAX_WIDTH } from '@/src/utils/holidays'

import HolidayBreadcrumbsWrapper from '@/src/components/common/holidays/holidayBreadcrumbs'
import PackageHeroWrapper from '@/src/components/common/holidays/packageHero'
import PackageSummaryWrapper from '@/src/components/common/holidays/packageSummary'
import HolidayNavTabsWrapper from '@/src/components/common/holidays/holidayNavTabs'
import ContactStripWrapper from '@/src/components/common/holidays/contactStrip'
import HolidaySectionWrapper from '@/src/components/common/holidays/holidaySection'
import EnquiryCardWrapper from '@/src/components/common/holidays/enquiryCard'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'
import type { RefundTimelineSource } from '@/src/utils/holidays/parseRefundTimeline'

interface HolidayPackageDesktopPageProps {
    holidayPackage: HolidayPackage
    navTabs: HolidayNavTab[]
    collectionLabel: string
    domainCountry: string
    /** The one refund schedule for this package, and the section it belongs to. */
    refundTimeline: RefundTimelineSource | null
}

const HolidayPackageDesktopPage: React.FC<HolidayPackageDesktopPageProps> = ({
    holidayPackage,
    navTabs,
    collectionLabel,
    refundTimeline,
    domainCountry
}) => {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                // `clip` not `hidden`: overflow-x:hidden makes this a scroll container, which
                // silently disables position:sticky for every descendant — including the enquiry
                // card. `clip` trims the same overflow without creating that container.
                overflowX: 'clip'
            }}
        >
            <ErrorBoundaryWrapper identifier="holidays-package-header-desktop">
                <HeaderContainer domainCountry={domainCountry} activeVertical="holidays" />
            </ErrorBoundaryWrapper>

            <Container maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH, pt: 2.5 }}>
                <ErrorBoundaryWrapper identifier="holidays-package-hero-desktop">
                    <PackageHeroWrapper gallery={holidayPackage.gallery} isMobile={false} />
                </ErrorBoundaryWrapper>
            </Container>

            {/* Breadcrumb and section tabs travel together and pin to the top on scroll. */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 11,
                    bgcolor: 'background.default',
                    py: 1.5,
                    mt: 2
                }}
            >
                <Container maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH }}>
                    <ErrorBoundaryWrapper identifier="holidays-package-breadcrumbs-desktop">
                        <Box sx={{ pb: 1.25 }}>
                            <HolidayBreadcrumbsWrapper
                                collection={{ slug: holidayPackage.collectionSlug, label: collectionLabel }}
                                currentLabel={holidayPackage.summary.destinationName}
                            />
                        </Box>
                    </ErrorBoundaryWrapper>

                    <ErrorBoundaryWrapper identifier="holidays-package-nav-desktop">
                        <HolidayNavTabsWrapper tabs={navTabs} isMobile={false} />
                    </ErrorBoundaryWrapper>
                </Container>
            </Box>

            <Container component="main" maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH, pb: 4 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 8 }}>
                        {/* One card holds the whole read: sections are `flat` and rule-separated. */}
                        <Box
                            sx={{
                                borderRadius: HOLIDAYS_RADIUS.SURFACE,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'borders.default',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
                                <ErrorBoundaryWrapper identifier="holidays-package-summary-desktop">
                                    <PackageSummaryWrapper summary={holidayPackage.summary} isMobile={false} />
                                </ErrorBoundaryWrapper>
                            </Box>

                            {holidayPackage.sections.map((section, index) => (
                                <ErrorBoundaryWrapper
                                    key={section.id}
                                    identifier={`holidays-package-section-${section.id}-desktop`}
                                >
                                    <HolidaySectionWrapper
                                        section={section}
                                        refundBands={
                                            refundTimeline?.sectionId === section.id ? refundTimeline.bands : undefined
                                        }
                                        refundReplacesBody={refundTimeline?.origin === 'cancellation'}
                                        collectionSlug={holidayPackage.collectionSlug}
                                        variant="flat"
                                        isFirst={index === 0}
                                        isMobile={false}
                                    />
                                </ErrorBoundaryWrapper>
                            ))}
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <ErrorBoundaryWrapper identifier="holidays-package-contact-bottom-desktop">
                                <ContactStripWrapper layout="inline" isMobile={false} />
                            </ErrorBoundaryWrapper>
                        </Box>
                    </Grid>

                    {/*
                     * alignSelf stretch is what makes the sticky card work: the grid row is as tall
                     * as the content column, and a sticky child can only travel inside its own
                     * parent's box. Without it the column collapses to the card's height and the
                     * card scrolls away.
                     */}
                    <Grid size={{ xs: 12, md: 4 }} sx={{ alignSelf: 'stretch' }}>
                        <Box sx={{ position: 'sticky', top: 136 }}>
                            <Box sx={{ mb: 2 }}>
                                <ErrorBoundaryWrapper identifier="holidays-package-contact-top-desktop">
                                    <ContactStripWrapper isMobile={false} />
                                </ErrorBoundaryWrapper>
                            </Box>

                            <ErrorBoundaryWrapper identifier="holidays-package-enquiry-desktop">
                                <EnquiryCardWrapper
                                    enquiry={holidayPackage.enquiry}
                                    startingPrice={holidayPackage.summary.startingPrice}
                                    isMobile={false}
                                />
                            </ErrorBoundaryWrapper>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            <Box sx={{ mt: 'auto' }}>
                <HolidaysFooter showHomeHighlights={false} />
            </Box>
        </Box>
    )
}

export { HolidayPackageDesktopPage }
export default HolidayPackageDesktopPage
